import React from 'react'
import { isArray, isNumber, isObject, startCase } from 'lodash'
import queryString from 'query-string'
import { CHANNEL_TYPE } from '@/utils/constants'

export const HARD_SELECTED_EVENT_ID = 'hard-selected-event'
export const EVENT_VIEWER_ID = 'event-viewer'

export const UNIX_TIMESTAMP_LENGTH = 10

// Voice-agent activity (AVR-550) — two extra Events-tab sections, only ever
// present on AI calls. Exported because the section keys are shared between the
// normalizer in callSearch.actions and the viewer that renders them.
export const TOOL_CALL_SECTION = 'tool_call'
export const AGENT_EVENT_SECTION = 'agent_event'
export const AGENT_ACTIVITY_SECTIONS = [TOOL_CALL_SECTION, AGENT_EVENT_SECTION]

export const SECTION_NAMES = {
  postcall: 'Post Call',
  deck: 'Dynamic Prompt',
  checklist: 'Checklist',
  notification: 'Notification',
  insight: 'Insights',
  // skb: 'Smart Knowledge Base',
  keywords: 'Keyword',
  [TOOL_CALL_SECTION]: 'Tool Call',
  [AGENT_EVENT_SECTION]: 'Agent Event',
}

export const getContextFromEvent = (event) => {
  const { section, category, item, decklist_item, criteria_name, scorecard_name } = event
  let contextStr = section
  // the api returns it without the s but we store it in valid
  // schema with it. want to update the api but there's some side
  // effects i would want to look out for first
  if (section === 'notification') {
    contextStr = 'notifications'
  }

  if (category) {
    contextStr += ` - ${category}`
  }
  if (item) {
    contextStr += ` - ${item}`
  }
  if (decklist_item) {
    contextStr += ` - ${decklist_item}`
  }
  if (criteria_name && scorecard_name) {
    // Not a label -- this whole function returns a matching key, compared with
    // === against the `events` query-string param (EventsViewer, CallExplorerPage)
    // and never rendered. Renaming the prefix would silently stop deep links
    // built against the old value from selecting their event, so it keeps the
    // Copilot name the links were generated with.
    contextStr = `QA Copilot - ${scorecard_name} - ${criteria_name}`
  }
  if (criteria_name && !scorecard_name) {
    contextStr = `keywords - ${criteria_name}`
  }

  return contextStr
}

export const roundTwoDecimals = (num) => Math.round(num * 100) / 100

// A scorecard whose criteria were all non-applicable comes back scored with a
// possible_score of 0. There is no percentage to show for it, and dividing by
// the 0 denominator would render NaN%.
export const formatScorePercent = (aggregateScore, possibleScore) => {
  if (!possibleScore) {
    return 'N/A'
  }

  return `${roundTwoDecimals((aggregateScore / possibleScore) * 100)}%`
}

export const calculateGrade = (score) => {
  if (!score.possible_score) {
    return {}
  }

  const scorePercent = score.aggregate_score / score.possible_score

  if (scorePercent * 100 > score.exceeds_threshold) {
    return { success: true }
  }
  if (scorePercent * 100 > score.meets_threshold) {
    return { caution: true }
  }
  if (scorePercent * 100 > score.improvement_threshold) {
    return { warning: true }
  }

  return { critical: true }
}

export const getInitialCriteriaValues = (score) => {
  // userId no longer needed — criteria notes used to be embedded in the
  // formik state and saved through update_criteria, but they now live in
  // the unified /api/notes flow (NotesPanel renders + saves them
  // independently). The criteria form just tracks scores now.
  return score.section_scores
    .flatMap((sectionScore) =>
      sectionScore.measure_scores.flatMap((measureScore) =>
        measureScore.criteria_scores.flatMap((criteriaScore) => ({
          uuid: criteriaScore.uuid,
          score: criteriaScore.score,
        }))
      )
    )
    .reduce((acc, { uuid, score }) => {
      // unfortunately due to the way radio groups work, the id needs to be the value so each value
      // needs to be unique for the entire form :(
      acc[uuid] = score
      return acc
    }, {})
}

export const CALL_EXPLORER_CRITERIA_FILTER_ENUMS = {
  ALL: 'all',
  INCOMPLETE: 'incomplete',
  COMPLETE: 'complete',
  PASSING: 'passing',
  FAILING: 'failing',
  DISPUTES: 'disputes',
  NA: 'na',
}

export const filterCriteriaScore = (criteriaScore, selectedCriteriaFilter) => {
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.ALL) {
    return true
  }
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.PASSING) {
    return criteriaScore.score === 1
  }
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.FAILING) {
    return criteriaScore.score === 0
  }
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.INCOMPLETE) {
    return !isNumber(criteriaScore.score)
  }
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.NA) {
    return criteriaScore.score === -1
  }
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.COMPLETE) {
    return isNumber(criteriaScore.score)
  }
  if (selectedCriteriaFilter === CALL_EXPLORER_CRITERIA_FILTER_ENUMS.DISPUTES) {
    return criteriaScore?.dispute?.status === 'pending'
  }
  return true
}

export const formatTime = (anchorTime) => {
  const totalSeconds = Math.floor(anchorTime) // Drop the milliseconds

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds - hours * 3600) / 60)
  const seconds = totalSeconds - hours * 3600 - minutes * 60

  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = seconds.toString().padStart(2, '0')

  let formattedTime = ''
  if (formattedHours !== '00') {
    formattedTime = `${formattedHours}:`
  }
  formattedTime += `${formattedMinutes}:${formattedSeconds}`

  return formattedTime
}

export const formatScorecardCriteriaType = (criteriaScore) => {
  if (criteriaScore.manual) {
    return 'Manual Evaluation'
  }
  if (criteriaScore.criteria_type === 'ai') {
    return 'AI'
  }
  if (criteriaScore.criteria_type === 'automated') {
    return 'Automated'
  }
  return null
}

export const CALL_EXPLORER_TAB_ENUMS = {
  SUMMARY: 'summary',
  DETAILS: 'details',
  EVENTS: 'events',
  QA_SCORES: 'qa_scores',
  ACTIVITY: 'activity',
  COMMENTS: 'comments',
  // Voice-agent transcript comments (voice-agents change-requests) — distinct
  // from COMMENTS above, which is the legacy CallNotesTab (Balto notes).
  AGENT_COMMENTS: 'agent_comments',
}

export const parseCallExplorerParams = (location) => {
  const { search } = location
  const parsed = queryString.parse(search)

  return parsed
}

export const updateCallExplorerParams = (location, history, params, paramsToRemoveList = []) => {
  const parsedParams = parseCallExplorerParams(location)
  const updatedParams = { ...parsedParams, ...params }
  paramsToRemoveList.forEach((param) => {
    delete updatedParams[param]
  })
  const search = queryString.stringify(updatedParams)
  history.push({ search })
}

export const removeCallExplorerParams = (location, history, paramsList) => {
  const parsedParams = parseCallExplorerParams(location)
  const updatedParams = { ...parsedParams }
  paramsList.forEach((param) => {
    delete updatedParams[param]
  })
  const search = queryString.stringify(updatedParams)
  history.push({ search })
}

export const canUserSave = (editRTQA, editQaCopilot, scorecardType) => {
  return (
    (scorecardType === 'automated' && editRTQA) || (scorecardType !== 'automated' && editQaCopilot)
  )
}

export const filterCallsData = (calls) => {
  return calls.filter((call) => {
    const isAttachmentOnlyEmail =
      call.record_type === CHANNEL_TYPE.EMAIL &&
      call.message_has_transcript !== true &&
      call.message_count === 1

    return !isAttachmentOnlyEmail
  })
}

export const formatExtractedValue = (value) => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const getItemKey = (parentKey, item) => {
  if (item === null || item === undefined) {
    return `${parentKey}-null`
  }
  if (isObject(item)) {
    return `${parentKey}-${JSON.stringify(item)}`
  }
  return `${parentKey}-${String(item)}`
}

export const renderExtractedDataRows = (data, depth = 1) => {
  return Object.entries(data).flatMap(([key, value]) => {
    const label = startCase(key)
    const indent = { paddingLeft: `${depth * 16}px` }

    if (value === null || value === undefined) {
      return (
        <tr key={key}>
          <td style={indent}>{label}</td>
          <td>—</td>
        </tr>
      )
    }

    if (isArray(value)) {
      return [
        <tr key={key} className="nested-header">
          <td style={indent}>
            <strong>{label}</strong>
          </td>
          <td>
            <em>({value.length} items)</em>
          </td>
        </tr>,
        ...value.flatMap((item, index) =>
          isObject(item) && item !== null ? (
            [
              <tr key={`${getItemKey(key, item)}-header`} className="nested-header">
                <td style={{ paddingLeft: `${(depth + 1) * 16}px` }}>
                  <strong>#{index + 1}</strong>
                </td>
                <td />
              </tr>,
              ...renderExtractedDataRows(item, depth + 2),
            ]
          ) : (
            <tr key={getItemKey(key, item)}>
              <td style={{ paddingLeft: `${(depth + 1) * 16}px` }}>#{index + 1}</td>
              <td>{formatExtractedValue(item)}</td>
            </tr>
          )
        ),
      ]
    }

    if (isObject(value)) {
      return [
        <tr key={key} className="nested-header">
          <td style={indent}>
            <strong>{label}</strong>
          </td>
          <td />
        </tr>,
        ...renderExtractedDataRows(value, depth + 1),
      ]
    }

    return (
      <tr key={key}>
        <td style={indent}>{label}</td>
        <td>{formatExtractedValue(value)}</td>
      </tr>
    )
  })
}
